import React from 'react';
import Navbar from './components/Navbar';
import ParentSection from './components/ParentSection';
import 'katex/dist/katex.min.css';

function App() {
  const parentSections = [
    {
      id: 'direct-coordinate-regression',
      title: 'Direct Coordinate Regression',
      sections: [
        {
          id: 'simple-cnn',
          title: 'Simple CNN',
          content: [
            {
              type: 'paragraph',
              text: 'Facial keypoint detection involves estimating the (x, y) positions of 68 landmarks—such as eyes, nose, and mouth—in grayscale images. We organize our methods into two main categories: [1] Direct Coordinate Regression, where we predict keypoint coordinates directly using (a) a simple CNN trained from scratch and (b) transfer learning with pretrained ResNet and DINO backbones, [2] Heatmap‑Based Detection, where we train a U‑Net from scratch to output per‑keypoint heatmaps and recover coordinates via argmax on those heatmaps.',
            },
            {
              type: 'paragraph',
              text: 'In this section, we demonstrate a simple convolutional neural network (CNN) architecture used for direct coordinate regression. The network is designed to learn precise keypoint coordinates directly from input grayscale images of size 1×224×224.'
            },
            {
              type: 'paragraph',
              text: 'Two variants of the architecture are implemented: SimpleNet and SimpleNet2. Both models use multiple convolutional blocks—each composed of a 3×3 convolution (with stride 1 and padding 1 to preserve spatial dimensions), followed by batch normalization, a ReLU activation, and max-pool.'
            },
            {
              type: 'paragraph',
              text: 'SimpleNet consists of three convolutional blocks. The first block converts the input into 32 feature maps, and subsequent blocks increase the feature depth to 64 and then 128, while reducing spatial dimensions from 224×224 to 112×112, then to 56×56, and finally to 28×28. The resulting feature maps are flattened and fed into a fully connected (FC) layer with 500 neurons. A dropout layer is applied for regularization before the final FC layer outputs 136 values corresponding to 68 keypoints (each with an x and y coordinate).'
            },
            {
              type: 'paragraph',
              text: 'SimpleNet2 builds on SimpleNet by adding a fourth convolutional block.'
            },
            {
              type: 'paragraph',
              text: 'We trained both SimpleNet and SimpleNet2 from scratch for 10 epochs using the Adam optimizer (batch size 16, dropout rate 0.5, SmoothL1Loss). To study the effect of learning rate, we ran each model with lr = 0.001 and lr = 0.01—yielding four experimental configurations: SimpleNet@0.001, SimpleNet@0.01, SimpleNet2@0.001, and SimpleNet2@0.01. The final validation loss (MSE) for the 4 configurations are 0.1822, 0.1805, 0.1731 and 0.1656 respectively. Below are the loss curves and visual results (note Green dots are GT, Purple are model predictions)'
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet@0.001 Loss', imageUrl: `${process.env.PUBLIC_URL}/images/training_loss_curve1.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet@0.001 Results', imageUrl: `${process.env.PUBLIC_URL}/images/keypoint_visualization_1.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet@0.01 Loss', imageUrl: `${process.env.PUBLIC_URL}/images/training_loss_curve2.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet@0.01 Results', imageUrl: `${process.env.PUBLIC_URL}/images/keypoint_visualization_2.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet2@0.001 Loss', imageUrl: `${process.env.PUBLIC_URL}/images/training_loss_curve3.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet2@0.001 Results', imageUrl: `${process.env.PUBLIC_URL}/images/keypoint_visualization_3.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet2@0.01 Loss', imageUrl: `${process.env.PUBLIC_URL}/images/training_loss_curve4.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'SimpleNet2@0.01 Results', imageUrl: `${process.env.PUBLIC_URL}/images/keypoint_visualization_4.jpg` },
              ]
            },
            {
              type: 'paragraph',
              text: 'Analyzing the four configurations, we observed only marginal improvements when increasing the learning rate. SimpleNet saw a decrease in validation MSE of 0.0017 (from 0.1822 to 0.1805), while SimpleNet2 improved by 0.0075 (from 0.1731 to 0.1656). The deeper SimpleNet2 consistently outperformed SimpleNet, indicating that additional capacity helps, but all models still converged to relatively high errors (>0.16). These results suggest that perhaps training a model from scratch to do direct coordinate regression is difficult.'
            }
          ],
        },
        {
          id: 'resnet',
          title: 'Transfer learning with ResNet',
          content: [
            {
              type: 'paragraph',
              text: 'We leveraged a ResNet18 backbone pretrained on ImageNet for keypoint regression. To adapt it to grayscale inputs, we replaced the first convolutional layer to accept a single channel by averaging the pretrained RGB weights across channels. We then swapped the classification head for a regression layer that outputs 136 values corresponding to 68 (x, y) keypoints. Training proceeded in two phases: with the backbone frozen, we trained only the new head for 5 epochs at lr = 10e-3; next, we unfroze and fine-tuned the entire network for 5 more epochs at lr = 10e-4. Throughout, we used SmoothL1Loss and the Adam optimizer.'
            },
            {
              type: 'paragraph',
              text: 'After the 10 epochs, the final validation loss (MSE) is 0.0064, which is a significant improvement in accuracy from training the SimpleNet and SimpleNet2 from scratch. Below is the loss curve and visual results (note Green dots are GT, Purple are model predictions)'
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'ResNet Loss', imageUrl: `${process.env.PUBLIC_URL}/images/resnet18_training_loss_nolog.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'ResNet Results', imageUrl: `${process.env.PUBLIC_URL}/images/keypoint_visualization_resnet18.jpg` },
              ]
            },
          ],
        },
        {
          id: 'dino',
          title: 'Transfer Learning with DINO',
          content: [
            {
              type: 'paragraph',
              text: 'We leveraged a DINO‑pretrained Vision Transformer (ViT‑Small, patch16_224) from timm as our backbone for keypoint regression. To handle grayscale inputs, we replaced the patch_embed.proj convolution to accept a single channel by averaging its pretrained RGB weights. We then attached a lightweight regression head—Linear(self.backbone.num_features→512) + ReLU + Dropout(0.3) + Linear(512→136). Training proceeded in two phases: first, with the DINO backbone frozen, we trained only the head for 5 epochs at lr = 0.001; then we unfroze the entire network and fine‑tuned for 5 more epochs at lr = 0.0001. Throughout, we used SmoothL1Loss and the Adam optimizer.'
            },
            {
              type: 'paragraph',
              text: 'After 10 total epochs, the final validation loss (MSE) was 0.179. Below is the loss curve and visual results (note Green dots are GT, Purple are model predictions)'
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'DINO Loss', imageUrl: `${process.env.PUBLIC_URL}/images/dino_training_loss_nolog.jpg` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'DINO Results', imageUrl: `${process.env.PUBLIC_URL}/images/keypoint_visualization_dino.jpg` },
              ]
            },
          ],
        },
        {
          id: 'comparison',
          title: 'Comparison',
          content: [
            {
              type: 'paragraph',
              text: 'Both SimpleNet variants trained from scratch and the fine-tuned DINO model predominantly regressed toward the dataset’s mean face—producing nearly identical landmark patterns across all inputs. In contrast, the ResNet18 transfer model captured individual facial variations and delivered substantially lower validation error.' 
            },
            {
              type: 'paragraph',
              text: 'A possible explanation as to why fine-tuning the DINO model did not work as well as fine-tuning ResNet is that ResNet18 is pretrained in a fully supervised manner on a classification task (ImageNet), where it learns features that are specifically optimized for distinguishing between known classes. These learned features tend to capture spatial and semantic information in a way that transfers well to localization tasks like keypoint detection. DINO, on the other hand, is trained using self-supervised learning. Although it learns rich semantic representations without labels, its objective is to achieve consistency across augmented views rather than to discriminate fine-grained spatial details required for precise keypoint regression. This may lead to features that are more abstract and less spatially localized, which can be a disadvantage for tasks that require high precision. Another reason is the architecture of these 2 models.' 
            },
            {
              type: 'paragraph',
              text: 'Another key factor is the architectural inductive bias: ResNet’s convolutional layers and pooling operations inherently preserve and aggregate fine‐grained spatial information, allowing the network to home in on small, localized features like the precise corner of an eye. In contrast, DINO’s Vision Transformer begins by splitting the image into relatively large patches (e.g. 16×16 pixels) and then applies global self‐attention across those patches. While this yields powerful high‐level semantics, it also blurs out sub‐patch positional nuance and lacks the strong local connectivity of CNNs—making it harder to recover the pixel‑perfect offsets required for accurate heatmap prediction.' 
            },
          ],
        }        
      ],
      
    },
    {
      id: 'heatmap-based-detection',
      title: 'Heatmap Based Detection',
      sections: [
        {
          id: 'unet-heatmap-prediction',
          title: 'UNet Heatmap Prediction',
          content: [
            {
              type: 'paragraph',
              text: 'Instead of regressing coordinates directly, we generate a set of ground‑truth heatmaps by centering a Gaussian kernel at each annotated keypoint and normalizing each map to [0,1]. This representation preserves the spatial structure and makes it easier for the network to learn localized responses.'
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'Image with corresponding keypoints and heatmap ', imageUrl: `${process.env.PUBLIC_URL}/images/example_heatmap.png` },
              ]
            },
            {
              type: 'paragraph',
              text: 'We implement a standard U‑Net encoder‑decoder with skip connections. For regularization, we add a dropout rate of 0.1 in the bottleneck. The network takes a single‑channel image and outputs 68 heatmaps (one per keypoint). During inference, we extract each landmark by taking the argmax over its predicted heatmap to recover (x,y) coordinates.'
            },
            {
              type: 'paragraph',
              text: 'Training uses MSE loss between predicted and ground‑truth heatmaps, optimized with AdamW with initial lr of  5 * 10e-4, weight decay of 10e-5 (batch size 16). After each epoch we evaluate on the validation set and feed the average validation loss into a ReduceLROnPlateau scheduler (halving the learning rate if there’s no improvement for two epochs). Below are the visualization of the results at various epochs for both the predicted heatmap and extracted keypoints.'
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'Epoch 5', imageUrl: `${process.env.PUBLIC_URL}/images/results_epoch_05.png` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'Epoch 10', imageUrl: `${process.env.PUBLIC_URL}/images/results_epoch_10.png` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'Epoch 15', imageUrl: `${process.env.PUBLIC_URL}/images/results_epoch_15.png` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'Epoch 20', imageUrl: `${process.env.PUBLIC_URL}/images/results_epoch_20.png` },
              ]
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'Epoch 25', imageUrl: `${process.env.PUBLIC_URL}/images/results_epoch_25.png` },
              ]
            },
            {
              type: 'paragraph',
              text: 'In the final model, the validation loss (MSE) for the heatmap prediction against ground truth heatmaps is 0.001584. Below is the training loss curve:'
            },
            {
              type: 'image-grid',
              columns: 1,
              images: [
                { title: 'UNet Training Loss', imageUrl: `${process.env.PUBLIC_URL}/images/unet2_train_loss_per_step.png` },
              ]
            },
            {
              type: 'paragraph',
              text: 'When we predict dense heatmaps instead of regressing keypoint coordinates directly, the network benefits from spatially structured supervision: each pixel contributes to the loss, so the model learns rich, location‑aware feature representations rather than trying to collapse all information into two numbers. This dense feedback both stabilizes training and encourages the network to focus on local context, which explains why heatmap‐based training outperforms the from‑scratch coordinate regressor (SimpleNet and SimpleNet2). We also experimented with varying sigma when generating the heatmaps. Using a larger sigma gave better results (since it provides better spatial supervision). Moreover, our heatmap model—despite being trained from scratch—achieves accuracy on par with a ResNet backbone fine‑tuned via transfer learning. '
            },
          ],
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-gray-300">
      {/* Navbar */}
      <Navbar parentSections={parentSections} />

      {/* Main Content */}
      <div className="flex-1 px-4 sm:px-2 lg:px-8 py-8 md:ml-64 w-full">
        <h1 className="text-3xl sm:text-2xl lg:text-4xl font-bold text-center mb-12 text-white">
          Keypoint Detection
        </h1>

        {/* Render Parent Sections */}
        {parentSections.map((parent) => (
          <ParentSection
            key={parent.id}
            id={parent.id}
            title={parent.title}
            sections={parent.sections}
          />
        ))}
      </div>
    </div>
  );
}

export default App;